import { Avatar, Flex, IconButton, Image, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useResource } from 'datx-jsonapi-react';
import { FC, useState } from 'react';

import ArrowLeftIcon from '@/assets/icons/ic-arrow-left.svg';

import { BlockedUserScreen } from '@/components/ui/shared/chat/ChatPopup/components/BlockedUserScreen';
import { ChatChannelOptions } from '@/components/ui/shared/chat/ChatPopup/components/ChatChannelOptions';
import { EditMessageInput } from '@/components/ui/shared/chat/ChatPopup/components/EditMessageInput';
import { MessageInput } from '@/components/ui/shared/chat/ChatPopup/components/MessageInput';
import { MessagesBox } from '@/components/ui/shared/chat/ChatPopup/components/MessagesBox';
import { ChatProfile } from '@/components/ui/shared/chat/ChatPopup/components/types';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { queryChatChannelOfUser } from '@/queries/event-chat-channels';
import { sessionQueryOptions } from '@/queries/upgrade/sessions';
import { blockedUserQueryOptions } from '@/queries/upgrade/thinProfiles';
import { ChatMessages } from '@/resources/ChatMessages';
import { getProfileAvatar, getProfileFullName, getProfileTitle } from '@/utils/profileHelpers';

interface IChatChannel {
  profile: ChatProfile;
  setProfileSelected: (profile: ChatProfile | null) => void;
}

export const ChatChannel: FC<IChatChannel> = ({ profile, setProfileSelected }) => {
  const { data: session } = useQuery(sessionQueryOptions());
  const main_profile = session?.main_profile;
  const [editMessage, setEditMessage] = useState<ChatMessages | null>(null);
  const { eventId } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });

  const { data } = useResource(queryChatChannelOfUser(profile.user?.id, eventId));
  const { data: blockedUserData, error } = useQuery(blockedUserQueryOptions(main_profile?.id, profile.user?.id));

  if (!error && blockedUserData && blockedUserData.length > 0) {
    return <BlockedUserScreen setProfileSelected={setProfileSelected} />;
  }

  return (
    <Flex h="full" w="full" px={{ base: 8, md: 5 }} py={6} flexDirection="column">
      <Flex w="full">
        <IconButton
          size="sm"
          onClick={() => setProfileSelected(null)}
          variant="unstyled"
          aria-label="Go back"
          icon={<ArrowLeftIcon />}
        />
        <Flex ml={2} cursor="pointer" key={profile.id}>
          {!getProfileAvatar(profile) && <Avatar bg="violet.500" color="white" name={getProfileFullName(profile)} />}
          {getProfileAvatar(profile) && (
            <Image
              flexShrink={0}
              src={getProfileAvatar(profile)}
              alt={getProfileFullName(profile)}
              width={10}
              height={10}
              style={{ borderRadius: '50%' }}
            />
          )}
          <Flex ml={2} flexDirection="column" justifyContent="center">
            <Text fontWeight="bold" fontFamily="heading">
              {getProfileFullName(profile)}
            </Text>
            <Text fontSize="xs">{getProfileTitle(profile)}</Text>
          </Flex>
        </Flex>
        <ChatChannelOptions setProfileSelected={setProfileSelected} channel={data} profile={profile} />
      </Flex>
      <MessagesBox setEditMessage={setEditMessage} channel={data} profile={profile} />
      {!editMessage && <MessageInput channel={data} profile={profile} />}
      {editMessage && (
        <EditMessageInput message={editMessage} setEditMessage={setEditMessage} channel={data} profile={profile} />
      )}
    </Flex>
  );
};
